import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { DesignSystem, getColor, getSpacing } from '../styles/designSystem';
import { TourismAPI, API_BASE_URL } from '../api/tourism';
import { ItineraryAPI, CombinedItinerary } from '../api/itinerary';
import AppHeader from './Header';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';
import CinematicHero from './ui/CinematicHero';
import { Container } from './ui';
import { TrekBackFace } from './FlipCard';
import FlipCard, { FlipCardSkeleton } from './FlipCard';

interface Destination {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  image: string;
  destinations: string[];
  state: string;
}

interface DestinationPageProps {
  destination: Destination | null;
  onBack: () => void;
  onNavigateTo?: (page: string, data?: any) => void;
}

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.4) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

// ============================================
// DESTINATION HERO DATA - Per-state dynamic content
// ============================================

const destinationHeroData: Record<string, {
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
  heroImage: string;
}> = {
  'ladakh': {
    titlePrefix: 'Journey through ',
    titleHighlight: 'Ladakh',
    subtitle: 'Where Himalayan peaks touch the sky, ancient monasteries whisper timeless stories, and high-altitude lakes mirror the heavens. Chart your mountain journey through Khardung La and Pangong, then embrace the alpine adventure as you trek Markha Valley and scale Kang Yatse — India\'s most breathtaking frontier awaits.',
    heroImage: `${API_BASE_URL}/api/images/destinationpage/ladakh/header_stok.jpg`,
  },
  'kashmir': {
    titlePrefix: 'Discover ',
    titleHighlight: 'Kashmir',
    subtitle: 'A paradise of emerald valleys, pristine Dal Lake houseboats, snow-dusted peaks, and saffron meadows. Chart your mountain journey from Srinagar\'s ancient gardens to Sonamarg\'s golden meadows, then embrace the alpine adventure into the Great Lakes and Kolahoi Glacier — India\'s crown of natural beauty.',
    heroImage: `${API_BASE_URL}/api/images/destinationpage/kashmir/home_sonamarg.jpg`,
  },
  'himachal': {
    titlePrefix: 'Explore ',
    titleHighlight: 'Himachal Pradesh',
    subtitle: 'From the misty cedar forests of Tirthan Valley to the stark beauty of Spiti and the charm of old Shimla. Chart your mountain journey through Manali, Kaza, and Chitkul, then embrace the alpine adventure across Hampta Pass and into Parashar\'s hidden highland — the land of the gods awaits.',
    heroImage: `${API_BASE_URL}/api/images/destinationpage/himachal/home_tirthan.jpg`,
  },
};

// ============================================
// STATE-SPECIFIC SECTION LABELS
// ============================================

const sectionLabels: Record<string, {
  roadsLabel: string;
  roadsTitle: string;
  roadsSubtitle: string;
  treksLabel: string;
  treksTitle: string;
  treksSubtitle: string;
  itineraryTitle: string;
  itinerarySubtitle: string;
}> = {
  'ladakh': {
    roadsLabel: 'Mountain Journeys',
    roadsTitle: 'Sacred Passes & Monasteries',
    roadsSubtitle: '',
    treksLabel: 'Alpine Adventures',
    treksTitle: 'Treks & Trails',
    treksSubtitle: '',
    itineraryTitle: 'YOUR LADAKH JOURNEY',
    itinerarySubtitle: 'Complete road trip packages through Ladakh\'s most breathtaking routes',
  },
  'kashmir': {
    roadsLabel: 'Mountain Journeys',
    roadsTitle: 'Valleys & Lake Country',
    roadsSubtitle: '',
    treksLabel: 'Alpine Adventures',
    treksTitle: 'Treks & Trails',
    treksSubtitle: '',
    itineraryTitle: 'YOUR KASHMIR JOURNEY',
    itinerarySubtitle: 'Complete road trip packages through Kashmir\'s most enchanting valleys',
  },
  'himachal': {
    roadsLabel: 'Mountain Journeys',
    roadsTitle: 'Mountain Passes & Temple Towns',
    roadsSubtitle: '',
    treksLabel: 'Alpine Adventures',
    treksTitle: 'Treks & Trails',
    treksSubtitle: '',
    itineraryTitle: 'YOUR HIMACHAL JOURNEY',
    itinerarySubtitle: 'Complete road trip packages through Himachal\'s most stunning landscapes',
  },
};

// Hero Section now uses shared CinematicHero component

// ============================================
// MAIN LAYOUT
// ============================================

const PageContainer = styled.div`
  background: #FFFFFF;
  min-height: 100vh;
  font-family: ${DesignSystem.typography.fonts.primary};
  overflow-x: hidden;
`;

// ============================================
// CONTENT SECTION
// ============================================

const ContentSection = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 80px 48px;
  
  @media (max-width: 768px) {
    padding: 40px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 32px 16px;
  }
`;

// Section Title with decorative line
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 48px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const SectionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const SectionLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #CAB96E;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.6rem, 5vw, 2.4rem);
  font-weight: 500;
  color: #CAB96E;
  margin: 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  word-spacing: 0.1em;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: clamp(1.4rem, 4vw, 1.8rem);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${getColor('primary.600')};
  margin: 4px 0 0 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.01em;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

// ============================================
// ITINERARY CARDS - Horizontal Scrolling
// ============================================

const CarouselContainer = styled.div`
  position: relative;
  width: calc(100% + 96px);
  margin-left: -48px;
  padding: 0 48px;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    padding: 0;
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 24px 0;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NavButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'left' ? 'left: 24px;' : 'right: 24px;'}
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  backdrop-filter: none;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  transition: all 0.3s ease;
  z-index: 100;
  font-size: 2rem;
  font-weight: 300;
  line-height: 1;
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${props => props.direction === 'left' ? 'left: 8px;' : 'right: 8px;'}
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    ${props => props.direction === 'left' ? 'left: 4px;' : 'right: 4px;'}
    font-size: 1.25rem;
  }
`;

// Premium Itinerary Card - Full Image with Text Overlay
const ItineraryCard = styled.div`
  flex: 0 0 280px;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 9 / 16;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  }
  
  @media (max-width: 768px) {
    flex: 0 0 240px;
  }
  
  @media (max-width: 480px) {
    flex: 0 0 min(220px, calc(100vw - 48px));
  }
`;

const CardImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const CardImage = styled.div<{ bgImage: string }>`
  width: 100%;
  height: 100%;
  background: url(${props => props.bgImage}) center/cover;
  transition: transform 0.6s ease;
  
  ${ItineraryCard}:hover & {
    transform: scale(1.08);
  }
`;

const CardBadges = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  z-index: 3;
`;

const DurationBadge = styled.span`
  background: #8B8372;
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const PriceBadge = styled.span`
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const CardBody = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 50%, transparent 100%);
  z-index: 2;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: white;
  margin: 0 0 8px 0;
  font-family: ${DesignSystem.typography.fonts.display};
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 16px 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 1px 4px rgba(0,0,0,0.2);
`;

const CardHighlights = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const HighlightChip = styled.span`
  background: #F3F4F6;
  color: #4B5563;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-top: 16px;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  span {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: #6B7280;
  }
`;

const ViewButton = styled.button`
  background: transparent;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  color: white;
  padding: 10px 24px;
  border-radius: 25px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: white;
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
`;

// ============================================
// ADVENTURES SECTION - Horizontal Scroll with Buttons
// ============================================

const AdventuresContainer = styled.div`
  position: relative;
  width: calc(100% + 96px);
  margin-left: -48px;
  padding: 0 48px;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    padding: 0;
  }
`;

const AdventuresScrollTrack = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overflow-y: visible;
  scroll-behavior: smooth;
  padding: 80px 0;
  margin: -60px 0;
  
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding: 24px 0;
    margin: 0;
    gap: 12px;
  }
`;

const AdventureScrollButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'left' ? 'left: -16px;' : 'right: -16px;'}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${getColor('primary.700')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 100;
  padding: 0;
  
  &:hover {
    color: ${getColor('primary.900')};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    ${props => props.direction === 'left' ? 'left: -12px;' : 'right: -12px;'}
  }
  
  @media (max-width: 480px) {
    ${props => props.direction === 'left' ? 'left: -8px;' : 'right: -8px;'}
  }
`;

const AdventureCard = styled.div`
  flex: 0 0 280px;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 9 / 16;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${cardEnter} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &:hover {
    box-shadow: 0 20px 40px rgba(202,185,110,0.15), 0 12px 32px rgba(0,0,0,0.2);
    z-index: 100;
  }
  
  @media (hover: none) {
    &:hover {
      transform: none;
    }
  }
  
  @media (max-width: 768px) {
    flex: 0 0 240px;
  }
  
  @media (max-width: 480px) {
    flex: 0 0 min(220px, calc(100vw - 48px));
  }
`;

const AdventureImage = styled.div<{ bgImage: string }>`
  width: 100%;
  height: 100%;
  background: url(${props => props.bgImage}) center/cover;
  background-position: center center;
  background-repeat: no-repeat;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg, 
      rgba(0,0,0,0.5) 0%, 
      rgba(0,0,0,0.2) 30%, 
      transparent 50%,
      rgba(0,0,0,0.3) 80%,
      rgba(0,0,0,0.7) 100%
    );
    z-index: 1;
    transition: background 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  ${AdventureCard}:hover &::before {
    background: linear-gradient(
      180deg, 
      rgba(0,0,0,0.6) 0%,
      rgba(0,0,0,0.3) 30%, 
      rgba(0,0,0,0.1) 50%,
      rgba(0,0,0,0.4) 80%,
      rgba(0,0,0,0.8) 100%
    );
  }
`;

const AdventureContent = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  left: 16px;
  z-index: 2;
  text-align: right;
`;

const AdventureTitle = styled.h4`
  color: white;
  font-size: 1.125rem;
  font-weight: 500;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 10px rgba(0,0,0,0.5);
  font-family: ${DesignSystem.typography.fonts.display};
  line-height: 1.3;
`;

const AdventureDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.6;
  text-shadow: 0 2px 8px rgba(0,0,0,0.7);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: 4.5em;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  word-wrap: break-word;
  word-break: break-word;
  
  ${AdventureCard}:hover & {
    -webkit-line-clamp: unset;
    max-height: 100%;
    overflow: visible;
    display: block;
    text-shadow: 0 2px 10px rgba(0,0,0,0.9);
  }
  
  @media (hover: none) {
    -webkit-line-clamp: unset;
    max-height: none;
    display: block;
  }
`;

const AdventureTypeBadge = styled.span`
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: #8B8372;
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 2;
`;

const ImageLightbox = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: opacity 0.3s ease;
  padding: 20px;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const LightboxSplitContainer = styled.div<{ $isPortrait?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 0;
  max-width: 1100px;
  max-height: 85vh;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    max-height: 95vh;
    overflow-y: auto;
    gap: 0;
    border-radius: 12px;
    background: white;
  }
`;

const LightboxImagePanel = styled.div<{ $isPortrait?: boolean }>`
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  min-width: 0;
  animation: ${slideInLeft} 0.4s ease-out;
  
  @media (max-width: 768px) {
    width: 100%;
    max-height: 50vh;
    flex: none;
  }
`;

const LightboxImageWrapper = styled.div`
  position: relative;
  display: inline-flex;
  max-height: 85vh;
  align-self: stretch;
  
  @media (max-width: 768px) {
    width: 100%;
    max-height: 50vh;
  }
`;

const LightboxImage = styled.img<{ $isPortrait?: boolean }>`
  max-width: 100%;
  max-height: 85vh;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    border-radius: 12px 12px 0 0;
    max-height: 50vh;
    width: 100%;
  }
`;

const LightboxDetailPanel = styled.div<{ $isPortrait?: boolean }>`
  width: 340px;
  flex-shrink: 0;
  align-self: stretch;
  animation: ${slideInRight} 0.4s ease-out;
  overflow-y: auto;
  
  /* Neutralize FlipCardFace / FlipCardBack styles for lightbox context */
  > div {
    position: relative !important;
    transform: none !important;
    backface-visibility: visible !important;
    -webkit-backface-visibility: visible !important;
    width: 100% !important;
    height: 100% !important;
    border-radius: 0 12px 12px 0;
    border-left: none;
    top: auto !important;
    left: auto !important;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    
    > div {
      border-radius: 0 0 12px 12px;
      border-left: 1px solid ${getColor('primary.200')};
      border-top: none;
      height: auto !important;
    }
  }
`;

const LightboxFallbackPanel = styled.div<{ $isPortrait?: boolean }>`
  width: 340px;
  flex-shrink: 0;
  align-self: stretch;
  background: white;
  border-radius: 0 12px 12px 0;
  display: flex;
  flex-direction: column;
  padding: 28px 22px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid ${getColor('primary.200')};
  border-left: none;
  animation: ${slideInRight} 0.4s ease-out;
  
  @media (max-width: 768px) {
    width: 100%;
    border-radius: 0 0 12px 12px;
    border-left: 1px solid ${getColor('primary.200')};
    border-top: none;
    min-height: 300px;
  }
`;

const FallbackTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 12px 0;
  font-family: ${DesignSystem.typography.fonts.display};
`;

const FallbackDescription = styled.p`
  font-size: 0.875rem;
  color: ${getColor('primary.800')};
  line-height: 1.6;
  margin: 0 0 auto 0;
`;

const FallbackBadge = styled.span`
  display: inline-block;
  background: #CAB96E;
  color: #1a1a1a;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 16px;
  align-self: flex-start;
`;

const LightboxImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
  max-height: 90vh;

  /* Road lightbox uses full border-radius on image */
  img {
    border-radius: 12px;
  }
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 2rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
`;

const LightboxTextOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 40px 24px 20px;
  color: white;
  text-align: center;
  animation: ${fadeInUp} 0.4s ease-out;
  z-index: 10;
`;

const LightboxTopOverlay = styled.div<{ $isPortrait?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 20px 20px 40px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 65%, transparent 100%);
  color: white;
  text-align: center;
  z-index: 10;
  border-radius: 12px 0 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    border-radius: 12px 12px 0 0;
  }
`;

const LightboxTopTitle = styled.h2<{ $isPortrait?: boolean }>`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.02em;
`;

const LightboxTopDescription = styled.p<{ $isPortrait?: boolean }>`
  font-size: 1.0rem;
  line-height: 1.6;
  margin: 0;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
  opacity: 0.92;
  max-width: 600px;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LightboxTitle = styled.h2`
  font-size: 1.65rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const LightboxType = styled.span`
  display: inline-block;
  background: #8B8372;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 8px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
`;

const CollapseChevron = styled.span<{ $collapsed: boolean }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-right: 2.5px solid currentColor;
  border-bottom: 2.5px solid currentColor;
  transform: ${props => props.$collapsed ? 'rotate(45deg)' : 'rotate(-135deg)'};
  transition: transform 0.3s ease;
`;

const CollapseButton = styled.button`
  background: transparent;
  border: none;
  color: ${getColor('primary.700')};
  cursor: pointer;
  padding: 12px;
  transition: color 0.3s ease;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${getColor('primary.900')};
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
`;

// ============================================
// GOLD DIVIDER — matches HomePage SectionDivider
// ============================================

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${getSpacing('xl')};
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    max-width: 120px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      #CAB96E 50%,
      transparent 100%
    );
  }
  
  & > span {
    color: #CAB96E;
    font-size: 0.5rem;
    margin: 0 ${getSpacing('md')};
    opacity: 0.7;
  }
`;

// ============================================
// PICK YOUR JOURNEY — Scroll Infrastructure (from HomePage)
// ============================================

const InfiniteScrollSection = styled.section<{ $warm?: boolean }>`
  position: relative;
  background: ${getColor('neutral.white')};
  padding: 48px 0;
  content-visibility: auto;
  contain-intrinsic-size: auto 600px;
  
  @media (max-width: 768px) {
    padding: 24px 0;
  }
  
  @media (max-width: 480px) {
    padding: 16px 0;
  }
`;

const PickJourneySectionHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: ${getSpacing('3xl')};
  padding: 0;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 0 ${getSpacing('md')};
    gap: ${getSpacing('sm')};
  }
`;

const PickJourneySectionTitle = styled.h2`
  font-size: clamp(1.6rem, 5vw, 2.4rem);
  font-weight: 500;
  color: #CAB96E;
  margin: 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  word-spacing: 0.1em;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: clamp(1.4rem, 4vw, 1.8rem);
  }
`;

const ExploreAllLink = styled.a`
  color: ${getColor('primary.900')};
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 400;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${getColor('primary.400')};
  padding: ${getSpacing('xs')} ${getSpacing('sm')};
  transition: all 225ms cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: absolute;
  right: 0;
  
  &:hover {
    opacity: 0.7;
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    position: static;
  }
`;

const MasonryContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
  padding: 0;
  max-width: 100%;
  margin: 0 auto;
`;

const ScrollableRow = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0;
  margin: 0;
  flex-wrap: nowrap;
  
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 1199px) {
    gap: 16px;
  }
  
  @media (max-width: 767px) {
    gap: ${getSpacing('md')};
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const ScrollChevron = styled.span<{ direction: 'left' | 'right' }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-right: 2.5px solid currentColor;
  border-bottom: 2.5px solid currentColor;
  transform: ${props => props.direction === 'left' ? 'rotate(135deg)' : 'rotate(-45deg)'};
  transition: transform 0.3s ease;
`;

const ScrollButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${p => p.direction === 'left' ? 'left: -40px;' : 'right: -40px;'}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${getColor('primary.700')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 100;
  padding: 0;
  
  &:hover {
    color: ${getColor('primary.900')};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    ${p => p.direction === 'left' ? 'left: -32px;' : 'right: -32px;'}
  }
  
  @media (max-width: 480px) {
    ${p => p.direction === 'left' ? 'left: -28px;' : 'right: -28px;'}
  }
`;

// ============================================
// COMPONENT
// ============================================

const DestinationPageNew: React.FC<DestinationPageProps> = ({ destination, onBack, onNavigateTo }) => {
  const roadsScrollRef = useRef<HTMLDivElement>(null);
  const treksScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [roadsAndTrails, setRoadsAndTrails] = useState<Array<any>>([]);
  const [treks, setTreks] = useState<Array<any>>([]);
  const [isRoadsCollapsed, setIsRoadsCollapsed] = useState(false);
  const [isTreksCollapsed, setIsTreksCollapsed] = useState(false);
  const [selectedAdventure, setSelectedAdventure] = useState<any>(null);
  const [destinationHeaders, setDestinationHeaders] = useState<{ [key: string]: string }>({});
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();
  const [trekItineraries, setTrekItineraries] = useState<CombinedItinerary[]>([]);
  const [isPortraitImage, setIsPortraitImage] = useState(false);

  // ─── Pick Your Journey FlipCard state ────────────────────────────────────
  const scrollItinerariesRef = useRef<HTMLDivElement>(null);
  const [flipCardItineraries, setFlipCardItineraries] = useState<any[]>([]);
  const [flippedCards, setFlippedCards] = useState<Set<number | string>>(new Set());
  const [isItineraryLoading, setIsItineraryLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const isScrollingRef = useRef(false);

  // ─── Fetch Pick Your Journey Itineraries for FlipCards ──────────────────
  useEffect(() => {
    if (!destination) return;
    const loadFlipCardItineraries = async () => {
      setIsItineraryLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/itineraries/destination-page?state=${encodeURIComponent(destination.state)}`);
        if (response.ok) {
          const data = await response.json();
          const items = data.itineraries || [];
          // Fisher-Yates shuffle
          for (let i = items.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [items[i], items[j]] = [items[j], items[i]];
          }
          setFlipCardItineraries(items);
        }
      } catch (error) {
        console.error('Error loading destination page itineraries:', error);
      } finally {
        setIsItineraryLoading(false);
      }
    };
    loadFlipCardItineraries();
  }, [destination]);

  // ─── Mobile detection for FlipCard tap behavior ─────────────────────────
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ─── FlipCard tap handler (mobile) ──────────────────────────────────────
  const handleFlipCardTap = useCallback((index: number | string) => {
    if (!isMobile || isScrollingRef.current) return;
    setFlippedCards(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.clear();
        next.add(index);
      }
      return next;
    });
  }, [isMobile]);

  const fetchCategories = useCallback(async () => {
    if (!destination) return;
    try {
      const response = await TourismAPI.getDestinationCategories(destination.state);
      // API response wraps data in { status, data: {...}, message, timestamp }
      const categoryData = response.data || response;
      setRoadsAndTrails(categoryData.roadsAndTrails || []);
      setTreks(categoryData.treks || []);
      console.log('✅ Categories loaded for', destination.state, '- Roads:', categoryData.roadsAndTrails?.length, 'Treks:', categoryData.treks?.length);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
    }
  }, [destination]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch destination header images from API
  useEffect(() => {
    const fetchDestinationHeaders = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/media/destination-headers`);
        if (response.ok) {
          const data = await response.json();
          setDestinationHeaders(data.destination_headers || {});
          console.log('✅ Destination headers loaded:', data.destination_headers);
        } else {
          console.warn('⚠️ Failed to fetch destination headers');
        }
      } catch (error) {
        console.error('❌ Error fetching destination headers:', error);
      }
    };

    fetchDestinationHeaders();
  }, []);

  useEffect(() => {
    if (destination) {
      fetchCategories();
    }
  }, [destination, fetchCategories]);

  // ─── Fetch Trek Itineraries (CombinedItinerary) for Split-View Panel ────
  useEffect(() => {
    if (!destination) return;
    const loadStateTreks = async () => {
      try {
        const results = await ItineraryAPI.getAvailableTreks(destination.state);
        setTrekItineraries(results);
      } catch (error) {
        console.error('Error loading trek itineraries for split-view:', error);
      }
    };
    loadStateTreks();
  }, [destination]);

  // Reset portrait detection when adventure changes
  useEffect(() => {
    setIsPortraitImage(false);
  }, [selectedAdventure]);

  // ─── Match selectedAdventure to a CombinedItinerary ─────────────────────
  const matchedItinerary = useMemo<CombinedItinerary | null>(() => {
    if (!selectedAdventure || trekItineraries.length === 0) return null;
    // Check if this adventure is a trek (exists in treks array)
    const isTrek = treks.some((t: any) => t.id === selectedAdventure.id);
    if (!isTrek) return null;

    const adventureTitle = (selectedAdventure.title || '').toLowerCase().trim();
    const adventureId = (selectedAdventure.id || '').toLowerCase().replace(/^trek_/, '').replace(/_/g, '-');

    // 1. Exact name match (case-insensitive)
    let match = trekItineraries.find(it => {
      const itName = (it.trekName || it.name || '').toLowerCase().trim();
      return itName === adventureTitle;
    });
    if (match) return match;

    // 2. ID normalization match
    match = trekItineraries.find(it => {
      const itId = (it.id || '').toLowerCase().replace(/^trek_/, '').replace(/_/g, '-');
      return itId === adventureId;
    });
    if (match) return match;

    // 3. Substring containment match
    match = trekItineraries.find(it => {
      const itName = (it.trekName || it.name || '').toLowerCase().trim();
      return itName.includes(adventureTitle) || adventureTitle.includes(itName);
    });
    return match || null;
  }, [selectedAdventure, trekItineraries, treks]);

  const scrollAdventures = (direction: 'left' | 'right', ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      const scrollAmount = 400;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!destination) return null;

  // Get per-destination hero data with API-fetched images or use fallbacks
  const headerImageFromAPI = destinationHeaders[destination.state];
  const heroData = destinationHeroData[destination.state] || {
    titlePrefix: 'Explore ',
    titleHighlight: destination.title,
    subtitle: destination.description || 'Discover breathtaking landscapes, ancient cultures, and unforgettable adventures in one of the world\'s most spectacular destinations.',
    heroImage: destination.image,
  };
  
  // Use API-fetched header image if available, otherwise fallback to default
  heroData.heroImage = headerImageFromAPI || heroData.heroImage;

  const totalAdventures = roadsAndTrails.length + treks.length;
  const labels = sectionLabels[destination.state] || sectionLabels['ladakh'];

  return (
    <PageContainer>
      <AppHeader 
        onNavigateTo={onNavigateTo} 
        currentPage="destination-page" 
        transparent
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* Hero Section — matching HomePage cinematic pattern */}
      <CinematicHero
        bgImage={heroData.heroImage}
        height="100vh"
        mobileHeight="100vh"
        brightness={1.15}
        contrast={1.1}
        title={<>{heroData.titlePrefix}<span>{heroData.titleHighlight}</span></>}
        subtitle={heroData.subtitle}
        tagline={<>
          {totalAdventures > 0 ? `${totalAdventures}+` : '14+'} Handcrafted Adventures
          <span className="separator">·</span>
          1–9 Day Journeys
          <span className="separator">·</span>
          Fully Customizable
        </>}
        ctaText="START YOUR ESCAPE"
        onCtaClick={() => onNavigateTo?.('curated-experiences', {
          destination: {
            id: destination?.id || 0,
            title: destination?.title || heroData.titleHighlight,
            description: destination?.description || '',
            category: destination?.category || '',
            duration: destination?.duration || '',
            image: destination?.image || '',
            destinations: destination?.destinations || [],
            state: destination?.state || 'ladakh'
          }
        })}
      />

      {/* Roads & Trails Section */}
      {roadsAndTrails.length > 0 && (
        <ContentSection ref={contentRef} style={{ paddingTop: '64px', paddingBottom: '64px' }}>
          <SectionHeader>
            <SectionTitleGroup>
              <SectionLabel>{labels.roadsLabel}</SectionLabel>
              <SectionTitle>{labels.roadsTitle}</SectionTitle>
            </SectionTitleGroup>
            <CollapseButton onClick={() => setIsRoadsCollapsed(!isRoadsCollapsed)}>
              <CollapseChevron $collapsed={isRoadsCollapsed} />
            </CollapseButton>
          </SectionHeader>

          {!isRoadsCollapsed && (
            <AdventuresContainer>
              <AdventureScrollButton direction="left" onClick={() => scrollAdventures('left', roadsScrollRef)}>
                <ScrollChevron direction="left" />
              </AdventureScrollButton>
              
              <AdventuresScrollTrack ref={roadsScrollRef}>
                {(roadsAndTrails.length > 3 ? [...roadsAndTrails, ...roadsAndTrails, ...roadsAndTrails] : roadsAndTrails).map((adventure, index) => (
                  <AdventureCard 
                    key={`${adventure.id || index}-${index}`} 
                    onClick={() => setSelectedAdventure(adventure)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AdventureImage className="adventure-image" bgImage={adventure.url} />
                    <AdventureContent>
                      <AdventureTitle>{adventure.title}</AdventureTitle>
                      <AdventureDescription>
                        {adventure.description || 'Experience scenic mountain drives and beautiful landscapes'}
                      </AdventureDescription>
                    </AdventureContent>
                    <AdventureTypeBadge>{adventure.type || 'Road Trip'}</AdventureTypeBadge>
                  </AdventureCard>
                ))}
              </AdventuresScrollTrack>
              
              <AdventureScrollButton direction="right" onClick={() => scrollAdventures('right', roadsScrollRef)}>
                <ScrollChevron direction="right" />
              </AdventureScrollButton>
            </AdventuresContainer>
          )}
        </ContentSection>
      )}

      <SectionDivider><span>◆</span></SectionDivider>

      {/* Treks & Mountains Section */}
      {treks.length > 0 && (
        <ContentSection style={{ background: '#FFFFFF', paddingTop: '64px', paddingBottom: '64px' }}>
          <SectionHeader>
            <SectionTitleGroup>
              <SectionLabel>{labels.treksLabel}</SectionLabel>
              <SectionTitle>{labels.treksTitle}</SectionTitle>
            </SectionTitleGroup>
            <CollapseButton onClick={() => setIsTreksCollapsed(!isTreksCollapsed)}>
              <CollapseChevron $collapsed={isTreksCollapsed} />
            </CollapseButton>
          </SectionHeader>

          {!isTreksCollapsed && (
            <AdventuresContainer>
              <AdventureScrollButton direction="left" onClick={() => scrollAdventures('left', treksScrollRef)}>
                <ScrollChevron direction="left" />
              </AdventureScrollButton>
              
              <AdventuresScrollTrack ref={treksScrollRef}>
                {(treks.length > 3 ? [...treks, ...treks, ...treks] : treks).map((adventure, index) => (
                  <AdventureCard 
                    key={`${adventure.id || index}-${index}`} 
                    onClick={() => setSelectedAdventure(adventure)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AdventureImage className="adventure-image" bgImage={adventure.url} />
                    <AdventureContent>
                      <AdventureTitle>{adventure.title}</AdventureTitle>
                      <AdventureDescription>
                        {adventure.description || 'Experience challenging mountain treks and peak adventures'}
                      </AdventureDescription>
                    </AdventureContent>
                    <AdventureTypeBadge>{adventure.type || 'Trek'}</AdventureTypeBadge>
                  </AdventureCard>
                ))}
              </AdventuresScrollTrack>
              
              <AdventureScrollButton direction="right" onClick={() => scrollAdventures('right', treksScrollRef)}>
                <ScrollChevron direction="right" />
              </AdventureScrollButton>
            </AdventuresContainer>
          )}
        </ContentSection>
      )}

      <SectionDivider><span>◆</span></SectionDivider>

      {/* Pick Your Journey — FlipCard Itineraries Section (matches HomePage) */}
      <InfiniteScrollSection id="pick-your-journey">
        <Container maxWidth="xl">
          <PickJourneySectionHeader>
            <div style={{ textAlign: 'center' }}>
              <PickJourneySectionTitle>{labels.itineraryTitle}</PickJourneySectionTitle>
              <SectionSubtitle style={{ textAlign: 'center', marginTop: '8px' }}>{labels.itinerarySubtitle}</SectionSubtitle>
            </div>
            <ExploreAllLink
              onClick={(e) => {
                e.preventDefault();
                onNavigateTo?.('curated-experiences', {
                  destination: {
                    id: destination?.id || 0,
                    title: destination?.title || '',
                    description: destination?.description || '',
                    category: destination?.category || '',
                    duration: destination?.duration || '',
                    image: destination?.image || '',
                    destinations: destination?.destinations || [],
                    state: destination?.state || 'ladakh'
                  }
                });
              }}
            >
              See all →
            </ExploreAllLink>
          </PickJourneySectionHeader>

          <MasonryContainer>
            <ScrollButton
              direction="left"
              onClick={() => {
                if (scrollItinerariesRef.current) {
                  scrollItinerariesRef.current.scrollBy({ left: -300, behavior: 'smooth' });
                }
              }}
              title="Scroll left"
            >
              <ScrollChevron direction="left" />
            </ScrollButton>

            <ScrollableRow ref={scrollItinerariesRef}>
              {isItineraryLoading && [1,2,3,4].map(i => <FlipCardSkeleton key={`skel-j-${i}`} />)}
              {flipCardItineraries.map((itinerary, idx) => (
                <FlipCard
                  key={itinerary.id || itinerary.name || `itinerary-${idx}`}
                  item={itinerary}
                  variant="journey"
                  index={idx}
                  isFlipped={flippedCards.has(idx)}
                  isMobile={isMobile}
                  onFlip={handleFlipCardTap}
                  onExplore={(item) => {
                    onNavigateTo?.('detailed-itinerary', {
                      state: item.state?.[0] || destination?.state || 'Ladakh',
                      itineraryId: item.id,
                      fromDestination: true
                    });
                  }}
                  onFrontClick={(item) => {
                    onNavigateTo?.('detailed-itinerary', {
                      state: item.state?.[0] || destination?.state || 'Ladakh',
                      itineraryId: item.id,
                      fromDestination: true
                    });
                  }}
                />
              ))}
            </ScrollableRow>

            <ScrollButton
              direction="right"
              onClick={() => {
                if (scrollItinerariesRef.current) {
                  scrollItinerariesRef.current.scrollBy({ left: 300, behavior: 'smooth' });
                }
              }}
              title="Scroll right"
            >
              <ScrollChevron direction="right" />
            </ScrollButton>
          </MasonryContainer>
        </Container>
      </InfiniteScrollSection>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* Image Lightbox - Split-View for Treks, Simple for Roads */}
      <ImageLightbox 
        isOpen={!!selectedAdventure}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setSelectedAdventure(null);
          }
        }}
      >
        <LightboxClose onClick={() => setSelectedAdventure(null)}>×</LightboxClose>
        {selectedAdventure && matchedItinerary ? (
          /* ─── Trek Split-View: Image + TrekBackFace ─── */
          <LightboxSplitContainer $isPortrait={isPortraitImage} onClick={(e) => e.stopPropagation()}>
            <LightboxImagePanel $isPortrait={isPortraitImage}>
              <LightboxImageWrapper>
                <LightboxImage 
                  src={selectedAdventure.url} 
                  alt={selectedAdventure.title}
                  $isPortrait={isPortraitImage}
                  style={!isPortraitImage ? { borderRadius: '12px 0 0 12px' } : undefined}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setIsPortraitImage(img.naturalHeight > img.naturalWidth * 1.15);
                  }}
                />
                <LightboxTopOverlay $isPortrait={isPortraitImage}>
                  <LightboxTopTitle $isPortrait={isPortraitImage}>{selectedAdventure.title}</LightboxTopTitle>
                  {selectedAdventure.description && (
                    <LightboxTopDescription $isPortrait={isPortraitImage}>{selectedAdventure.description}</LightboxTopDescription>
                  )}
                </LightboxTopOverlay>
              </LightboxImageWrapper>
            </LightboxImagePanel>
            <LightboxDetailPanel $isPortrait={isPortraitImage}>
              <TrekBackFace
                item={matchedItinerary}
                onExplore={(item) => {
                  setSelectedAdventure(null);
                  onNavigateTo?.('trek-detail', {
                    state: item.state?.[0] || destination.state,
                    itineraryId: item.id,
                    fromDestination: true,
                  });
                }}
              />
            </LightboxDetailPanel>
          </LightboxSplitContainer>
        ) : selectedAdventure && treks.some((t: any) => t.id === selectedAdventure.id) ? (
          /* ─── Trek without matched itinerary: Image + Fallback Panel ─── */
          <LightboxSplitContainer $isPortrait={isPortraitImage} onClick={(e) => e.stopPropagation()}>
            <LightboxImagePanel $isPortrait={isPortraitImage}>
              <LightboxImageWrapper>
                <LightboxImage 
                  src={selectedAdventure.url} 
                  alt={selectedAdventure.title}
                  $isPortrait={isPortraitImage}
                  style={!isPortraitImage ? { borderRadius: '12px 0 0 12px' } : undefined}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setIsPortraitImage(img.naturalHeight > img.naturalWidth * 1.15);
                  }}
                />
                <LightboxTopOverlay $isPortrait={isPortraitImage}>
                  <LightboxTopTitle $isPortrait={isPortraitImage}>{selectedAdventure.title}</LightboxTopTitle>
                  {selectedAdventure.description && (
                    <LightboxTopDescription $isPortrait={isPortraitImage}>{selectedAdventure.description}</LightboxTopDescription>
                  )}
                </LightboxTopOverlay>
              </LightboxImageWrapper>
            </LightboxImagePanel>
            <LightboxFallbackPanel $isPortrait={isPortraitImage}>
              <FallbackTitle>{selectedAdventure.title}</FallbackTitle>
              {selectedAdventure.description && (
                <FallbackDescription>{selectedAdventure.description}</FallbackDescription>
              )}
              <FallbackBadge>{selectedAdventure.type || 'Trek'}</FallbackBadge>
            </LightboxFallbackPanel>
          </LightboxSplitContainer>
        ) : selectedAdventure ? (
          /* ─── Road/Other: Original fullscreen image ─── */
          <LightboxImageContainer>
            <LightboxImage src={selectedAdventure.url} alt={selectedAdventure.title} />
            <LightboxTextOverlay>
              <LightboxTitle>{selectedAdventure.title}</LightboxTitle>
              {selectedAdventure.description && (
                <p style={{ margin: '12px 0 0 0', fontSize: '0.95rem', lineHeight: '1.5', textShadow: '0 2px 6px rgba(0, 0, 0, 0.5)' }}>
                  {selectedAdventure.description}
                </p>
              )}
              <LightboxType>{selectedAdventure.type || 'Adventure'}</LightboxType>
            </LightboxTextOverlay>
          </LightboxImageContainer>
        ) : null}
      </ImageLightbox>
    </PageContainer>
  );
};

export default DestinationPageNew;
